import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  inject,
  OnChanges,
  booleanAttribute,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { Pagination } from '@app/shared/model/pagination.model';
import { ApiService } from '@core/providers/http';
import {
  BolsaService,
  BolseiroService,
  GenericaService,
} from '../../../api/services';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { Subject, takeUntil, tap } from 'rxjs';
import { toExtensoComMoeda } from '@app/core/helpers/toExtensoComMoeda';

interface VerificarRegraPagamentoPayload {
  id: string;
  pedidoID: number;
  bolseiroID: number;
  requerenteID: number;
  isActive: boolean;
  tipoBolsa: number;
  paisID: number;
  instituicaoGestoraID: number;
  nivelAcademico: number;
  areaProgramaFormacaoID: number;
  programaFormacaoID: number;
  anoCurricularFrequencia: string;
  valorSubsidio: number;
}

@Component({
  selector: 'app-declaracao-para-embaixada',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Select,
    InputTextModule,
    DataVoidComponent,
  ],
  providers: [ApiService, BolseiroService, BolsaService, GenericaService],
  templateUrl: './declaracao-para-embaixada.component.html',
  styleUrl: './declaracao-para-embaixada.component.scss',
})
export class DeclaracaoParaEmbaixadaComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Output() formValueChanges = new EventEmitter<{
    data: any;
    valid: boolean;
  }>();
  @Input() bolseiroID!: string;
  @Input() documento!: any;
  @Input() paisBolsa!: number;
  @Input({ transform: booleanAttribute }) disabled = false;

  private destroy$ = new Subject<void>();

  fb = inject(FormBuilder);
  bolsaService = inject(BolsaService);
  bolseiroService = inject(BolseiroService);
  genericaService = inject(GenericaService);

  form!: FormGroup;
  pagination: Pagination<any> = new Pagination<any>();
  estudantes: Array<{ id: number; nome: string }> = [];
  paisGenericoList: any[] = [];
  bolsa: any;
  paisID!: number;

  idiomas = [
    { label: 'Português', value: 'pt' },
    { label: 'Inglês', value: 'en' },
  ];

  ngOnInit(): void {
    this.buildForm();
    this.getPaisGenerico();
    // this.syncDisabled();
    this.wireCoreChanges();
  }

  ngOnChanges(): void {
    // this.syncDisabled();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ------------ Form ------------
  private buildForm(): void {
    this.form = this.fb.group({
      paisBolsa: ['', [Validators.required]],
      estudanteID: ['', Validators.required],
      bolsaID: ['', [Validators.required]],
      valores: [{ value: '', disabled: true }, [Validators.required]],
      valoresextenso: [{ value: '', disabled: true }, [Validators.required]],
      moeda: [{ value: '', disabled: false }],
      idioma: ['en', [Validators.required]],
    });
  }

  private syncDisabled(): void {
    if (!this.form) return;
    if (this.disabled) this.form.disable({ emitEvent: false });
    else this.form.enable({ emitEvent: false });
  }

  // ------------ Subscrições centrais ------------
  private wireCoreChanges(): void {
    // Emitir payload sempre que houver mudança relevante
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitPayload());

    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitPayload());

    // Reagir à mudança de estudante → recarrega bolsas
    this.form
      .get('estudanteID')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.resetBolsaCampos();
        if (!value) return;
        this.loadAllBolsa({ BolseiroID: value });
      });
  }

  private emitPayload(): void {
    const data = {
      ...this.form.getRawValue(),
      bolsaID: this.form.get('bolsaID')?.value,
      valores: String(this.form.get('valores')?.value ?? ''),
      idioma: this.form.get('idioma')?.value,
      tipoDocumento: 4,
    };
    this.formValueChanges.emit({ data, valid: this.form.valid });
  }

  private resetBolsaCampos(): void {
    this.form.patchValue(
      { bolsaID: null, valores: '', valoresextenso: '', moeda: '' },
      { emitEvent: false }
    );
    this.bolsa = null;
    this.pagination = new Pagination<any>().deserialize([]);
  }

  // ------------ UI Helpers ------------
  onPaisChange(): void {
    this.paisID = this.form.get('paisBolsa')?.value;
    if (!this.paisID) return;

    this.getEstudantesPorPais(this.paisID);
    this.form.patchValue({ estudanteID: null, bolsaID: null });
    this.pagination = new Pagination<any>().deserialize([]);
  }

  getSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    switch (status) {
      case 'Activo':
      case 'Finalizado':
        return 'success';
      case 'Inactivo':
        return 'warn';
      case 'Suspenso':
        return 'danger';
      case 'Pendente':
        return 'info';
      default:
        return 'warn';
    }
  }

  // ------------ Data ------------
  getPaisGenerico(): void {
    this.genericaService
      .listPaisGenerico({ tipoBolsa: 2 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) =>
          (this.paisGenericoList = response?.data || response || []),
        error: (e) => console.error('Erro ao carregar países genéricos:', e),
      });
  }

  getEstudantesPorPais(paisID: number): void {
    this.bolsaService
      .list({ pageSize: 100, OrderBy: 'id desc', PaisID: paisID })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          const data = resp?.data || [];
          this.estudantes = data.map((bolsa: any) => ({
            id: bolsa?.bolseiro?.id ?? bolsa?.id,
            nome: bolsa?.bolseiro?.nome ?? 'Estudante sem nome',
          }));
        },
        error: (e) => console.error('Erro ao carregar estudantes por país:', e),
      });
  }

  onPageChange(event: any): void {
    this.pagination.pageNumber = (event?.page ?? 0) + 1;
    this.pagination.pageSize = event?.rows ?? 5;
    this.loadAllBolsa({ BolseiroID: this.form.get('estudanteID')?.value });
  }

  loadAllBolsa(queryParams: any = {}): void {
    const params = {
      ...queryParams,
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 5,
    };

    this.bolsaService
      .list({
        ...params,
        BolseiroID: queryParams.BolseiroID || this.bolseiroID,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.bolsa = response?.data?.[0];
          this.form.get('bolsaID')?.setValue(this.bolsa?.id);
          this.pagination = new Pagination<any>().deserialize(response);
          this.fetchValorByRegra(this.bolsa);
        },
        error: (e) => console.error('Erro ao carregar bolsa:', e),
      });
  }

  private fetchValorByRegra(bolsa: any): void {
    if (!bolsa) return;
    const body = this.buildRegraPayload(bolsa);

    this.bolsaService
      .verificarRegraPagamento(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // tenta valor direto
          let valor = Number(res?.valorPagamento ?? 0);
          let moeda = '-';

          // fallback pela lista de regras
          if (!valor || Number.isNaN(valor)) {
            const lista = Array.isArray(res?.regraPagamentoSubsidio)
              ? res.regraPagamentoSubsidio
              : [];
            const apoioFormacao =
              lista.find((x: any) => x?.subsidioId === 2) ??
              lista.find((x: any) =>
                String(x?.subsidios?.descricao ?? '')
                  .toLowerCase()
                  .includes('apoio a formação')
              );
            valor = Number(apoioFormacao?.valor ?? 0);
            moeda = apoioFormacao?.moeda ?? '-';
          }

          const valorFmt = this.formatPtBR(valor || 0);
          const valorExtenso = this.toExtensoSafe(valor);

          // define os três campos de uma vez, sem disparar loops desnecessários
          this.form.patchValue(
            {
              valores: valorFmt,
              valoresextenso: valorExtenso,
              moeda,
            },
            { emitEvent: true } // queremos refletir no payload
          );
        },
        error: () => {
          this.form.patchValue(
            { valores: '0,00', valoresextenso: 'zero', moeda: '-' },
            { emitEvent: true }
          );
        },
      });
  }

  // ------------ Helpers de domínio ------------
  private buildRegraPayload(bolsa: any): VerificarRegraPagamentoPayload {
    const matricula = this.pickMatricula(bolsa);
    const anoCurricular = matricula?.anoCurricular ?? bolsa?.anoCurricular ?? 1;

    const programaFormacaoID =
      bolsa?.programaFormacaoID ??
      bolsa?.areaProgramaFormacao?.programaFormacaoId ??
      bolsa?.areaProgramaFormacao?.programaFormacaoID ??
      0;

    const bolseiroID = Number(bolsa?.bolseiroID ?? bolsa?.bolseiro?.id ?? 0);

    return {
      id: '0',
      pedidoID: Number(bolsa?.pedidoID ?? 0),
      bolseiroID,
      requerenteID: Number(bolsa?.requerenteID ?? 0),
      isActive: Boolean(bolsa?.isActive ?? true),
      tipoBolsa: Number(bolsa?.tipoBolsa ?? 0),
      paisID: Number(bolsa?.paisID ?? 0),
      instituicaoGestoraID: Number(
        bolsa?.instituicaoGestoraID ?? bolsa?.instituicaoGestora?.id ?? 0
      ),
      nivelAcademico: Number(bolsa?.nivelAcademico ?? 0),
      areaProgramaFormacaoID: Number(
        bolsa?.areaProgramaFormacaoID ?? bolsa?.areaProgramaFormacao?.id ?? 0
      ),
      programaFormacaoID: Number(programaFormacaoID),
      anoCurricularFrequencia: String(anoCurricular),
      valorSubsidio: Number(bolsa?.valorSubsidio ?? 0),
    };
  }

  private pickMatricula(b: any) {
    const arr = Array.isArray(b?.matricula) ? b.matricula : [];
    if (!arr.length) return null;

    const emCurso = arr.find(
      (m: any) => (m?.estadoDescricao || '').toLowerCase() === 'em curso'
    );
    if (emCurso) return emCurso;

    const parse = (d: any) => (d ? new Date(d).getTime() : 0);
    return (
      arr
        .slice()
        .sort(
          (a: any, c: any) => parse(c?.dataInicio) - parse(a?.dataInicio)
        )[0] || null
    );
  }

  // ------------ Utils ------------
  private formatPtBR(n: number): string {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n || 0);
  }

  private toExtensoSafe(n: number): string {
    if (!Number.isFinite(n)) return '';
    try {
      return toExtensoComMoeda(n, this.form.get('moeda')?.value || '');
    } catch {
      return '';
    }
  }

  parsePtNumber(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    let s = String(value).replace(/[^\d.,-]/g, '');
    s = s.replace(/\.(?=\d{3}(\D|$))/g, '');
    s = s.replace(',', '.');
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
}
